<!--
  Session settings row — shown below the main controls toolbar.
  Compact controls for mid-session audio parameter tuning.
  See REALTIME_SESSION_CONFIG.userSettings and consumer.py ALLOWED_SETTINGS.
-->
<template>
    <div
        class="flex flex-wrap sm:flex-nowrap items-center justify-center gap-2 sm:gap-4 rounded-2xl bg-white/90 backdrop-blur-sm px-2 sm:px-4 py-2 sm:py-2 shadow-md max-w-[calc(100vw-2rem)]">
        <!-- Microphone type: icon buttons with tooltip -->
        <div class="flex items-center gap-2 sm:gap-2">
            <span class="details font-semibold text-control-label whitespace-nowrap">{{ t("components.realtimeSettings.noiseReduction") }}</span>
            <div class="flex rounded-lg border border-control-border overflow-hidden">
                <BaseTooltip v-for="option of micOptions" :key="option.value">
                    <template #trigger>
                        <button
                            type="button"
                            class="flex items-center justify-center w-8 h-7 sm:w-9 sm:h-8 transition-colors"
                            :class="localSettings.microphone === option.value ? 'bg-control-accent text-ink-inverse' : 'text-control-text hover:bg-surface-hover'"
                            :aria-label="option.label"
                            @click="updateSetting('microphone', option.value)">
                            <i :class="['bi text-sm sm:text-base', micIcons[option.value]]" aria-hidden="true"></i>
                        </button>
                    </template>
                    <template #content>{{ option.description }}</template>
                </BaseTooltip>
            </div>
        </div>

        <!-- Divider (hidden when wrapped on mobile) -->
        <div class="hidden sm:block w-px h-6 bg-border" aria-hidden="true"></div>

        <!-- Interrupt mode toggle -->
        <BaseTooltip>
            <template #trigger>
                <BaseButton
                    theme="ghost"
                    class="flex items-center gap-1 rounded-lg px-2 py-1 sm:gap-2 sm:px-2.5 sm:py-1 transition-colors min-h-7 sm:min-h-[32px]"
                    :class="localSettings.allowInterruptions ? 'bg-toggle-option-selected text-control-text-strong' : 'text-ink-muted hover:bg-surface-hover'"
                    @click="updateSetting('allowInterruptions', !localSettings.allowInterruptions)">
                    <i :class="['bi text-xs sm:text-sm', localSettings.allowInterruptions ? 'bi-hand-index-fill' : 'bi-hand-index']" aria-hidden="true"></i>
                    <span class="details font-medium whitespace-nowrap">{{
                        localSettings.allowInterruptions ? t("components.realtimeSettings.interruptionsOn") : t("components.realtimeSettings.interruptionsOff")
                    }}</span>
                </BaseButton>
            </template>
            <template #content>{{ t("components.realtimeSettings.interruptionsTipOn") }}<br />{{ t("components.realtimeSettings.interruptionsTipOff") }}</template>
        </BaseTooltip>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import type { UserSessionSettings } from "~vue/composables/useRealtimeSession";
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// Listed as literal t() calls (not a config-driven lookup) so the vue-i18n ESLint catalogue
// check can see these chat.voice.settings.microphone.* keys are used.
const micOptions = computed(() => [
    { value: "far_field" as const, label: t("chat.voice.settings.microphone.farField.label"), description: t("chat.voice.settings.microphone.farField.description") },
    { value: "near_field" as const, label: t("chat.voice.settings.microphone.nearField.label"), description: t("chat.voice.settings.microphone.nearField.description") },
    { value: "off" as const, label: t("chat.voice.settings.microphone.off.label"), description: t("chat.voice.settings.microphone.off.description") },
]);

// Map mic values to Bootstrap Icons
const micIcons: Record<string, string> = {
    near_field: "bi-headphones",
    far_field: "bi-laptop",
    off: "bi-slash-circle",
};

const props = defineProps<{
    /** Current confirmed settings from the server */
    currentSettings: UserSessionSettings;
}>();

const emit = defineEmits<{
    /** Fired when the user changes a setting — parent sends it over WS */
    (e: "update", settings: Partial<UserSessionSettings>): void;
}>();

// Local copy that updates optimistically and syncs from server confirmation
const localSettings = reactive<UserSessionSettings>({
    pace: props.currentSettings.pace,
    microphone: props.currentSettings.microphone,
    allowInterruptions: props.currentSettings.allowInterruptions,
});

// Sync from server-confirmed values
watch(
    () => props.currentSettings,
    (confirmed) => {
        localSettings.pace = confirmed.pace;
        localSettings.microphone = confirmed.microphone;
        localSettings.allowInterruptions = confirmed.allowInterruptions;
    },
    { deep: true },
);

function updateSetting<K extends keyof UserSessionSettings>(key: K, value: UserSessionSettings[K]) {
    localSettings[key] = value;
    emit("update", { [key]: value });
}
</script>
